<template>
    <!-- Předáme breadcrumbsLinks do ListWrapper jako prop -->
    <ListWrapper
        :breadcrumb-links="breadcrumbLinks"
        :filtered-data="filteredData"
        :list-data="snippets"
        :toolbar-config="toolbarConfig"
        :pagination="pagination"
    >
        <main>
            <AlertBox
                v-if="pagination.data.length === 0"
                state="warning"
                :icon="MdiInformation"
                :title="t('Snippets')"
            >
                <p class="py-1 text-sm">{{ t('notFoundText') }}</p>
            </AlertBox>
            <ul
                v-if="pagination.data.length > 0"
                class="divide-y divide-gray-200"
            >
                <template
                    v-for="(snippet, index) in pagination.data"
                    :key="index"
                >
                    <li>
                        <router-link
                            :to="{
                                name: 'snippet',
                                params: {
                                    snippetid: snippet.snippetId,
                                },
                            }"
                            class="block hover:bg-gray-50"
                        >
                            <div class="flex items-center py-4">
                                <div class="flex min-w-0 flex-1 items-center">
                                    <div
                                        v-tippy="snippet.snippetId"
                                        class="shrink-0 px-2 text-sm font-medium text-gray-600"
                                    >
                                        <MdiFile class="h-7 w-7 shrink-0 text-blue-600" />
                                    </div>
                                    <div
                                        class="min-w-0 flex-1 px-4 md:grid md:grid-cols-3 md:gap-4"
                                    >
                                        <div>
                                            <p class="truncate text-sm font-medium text-blue-600">
                                                {{ snippet.name }}
                                            </p>
                                            <p class="mt-2 flex items-center text-sm text-gray-500">
                                                <MdiFileCog
                                                    class="mr-1.5 h-5 w-5 shrink-0 text-gray-400"
                                                />
                                                <span class="truncate">
                                                    {{ snippet.type }}
                                                </span>
                                            </p>
                                        </div>
                                        <div class="hidden md:block">
                                            <div>
                                                <p class="text-xs text-gray-900">
                                                    {{ t('created') }}:
                                                    <strong class="mx-1">
                                                        {{ snippet.createdBy }}
                                                    </strong>
                                                    <time :datetime="snippet.created">
                                                        {{
                                                            $dayjs(snippet.created).format(
                                                                'D. M. YYYY HH:mm',
                                                            )
                                                        }}
                                                    </time>
                                                </p>
                                                <p
                                                    v-if="snippet.updated"
                                                    class="text-xs text-gray-900"
                                                >
                                                    {{ t('updated') }}:
                                                    <strong class="mx-1">
                                                        {{ snippet.updatedBy }}
                                                    </strong>
                                                    <time :datetime="snippet.updated">
                                                        {{
                                                            $dayjs(snippet.updated).format(
                                                                'D. M. YYYY HH:mm',
                                                            )
                                                        }}
                                                    </time>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <MdiChevronRight class="mr-2 h-5 w-5 text-gray-400" />
                                </div>
                            </div>
                        </router-link>
                    </li>
                </template>
            </ul>
        </main>
    </ListWrapper>
</template>

<script setup>
    import { reactive, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import AlertBox from '../ui/AlertBox.vue';
    import { listSnippets } from '../composables/useSnippets';
    import { t } from '../composables/i18n';
    import { cloneData } from '../composables/utils';
    import { pageize } from '../composables/usePaginator';

    import {
        MdiFile,
        MdiFileCog,
        MdiChevronRight,
        MdiPlus,
        MdiInformation,
    } from 'materialdesignicons-vue3/icons/';
    import ListWrapper from '../ui/ListWrapper.vue';

    const route = useRoute(),
        router = useRouter(),
        snippets = reactive((await listSnippets()) || []);
    const filteredData = reactive(cloneData(snippets));

    const breadcrumbLinks = [
        {
            name: 'dashboard',
            title: t('Dashboard'),
        },
        {
            name: 'snippets',
            title: t('Snippets'),
            final: true,
        },
    ];

    const toolbarConfig = reactive({
        indexId: 'snippetId',
        search: {
            show: true,
            subject: route.query.search || '',
            keys: ['name', 'type'],
        },
        buttons: [
            {
                action: () => {
                    router.push({
                        name: 'snippet',
                        params: {
                            snippetid: '0',
                        },
                    });
                },
                show: true,
                title: t('buttonNewSnippet'),
                icon: MdiPlus,
            },
        ],
    });

    const itemsPerPage = 15;
    const pagination = reactive(
        pageize(route, filteredData, parseInt(route.params.page || 1), itemsPerPage),
    );

    const refresh = () => {
        const proxy = pageize(route, filteredData, parseInt(route.params.page || 1), itemsPerPage);
        Object.assign(pagination, proxy);
    };

    watch(route, () => {
        refresh();
    });

    watch(filteredData, () => {
        if (filteredData.length !== pagination.itemCount) {
            refresh();
        }
    });
</script>
